import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createEmailTemplate,
  deleteEmailTemplate,
  fetchEmailTemplateById,
  fetchEmailTemplates,
  updateEmailTemplate,
} from './EmailTemplates.services.ts';
import { toast } from 'react-toastify';
import i18n from '../../utils/i18n.ts';
import type { ApiError } from '../../types/App.ts';
import { useMsal } from '@azure/msal-react';

export const useService = ({ emailTemplateId }: { emailTemplateId: number | null }) => {
  const { accounts } = useMsal();
  const queryClient = useQueryClient();

  const hasAccount = accounts.length > 0;
  const createMutation = useMutation({
    mutationFn: createEmailTemplate,
    onSuccess: () => {
      toast.success(i18n.t('success.api.post-item', { item: i18n.t('module.email-template') }));
      queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
    },
    onError: (error: ApiError) => {
      toast.error(error?.response?.data?.message || error.message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: updateEmailTemplate,
    onSuccess: () => {
      toast.success(i18n.t('success.api.put-item', { item: i18n.t('module.email-template') }));
      queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
    },
    onError: (error: ApiError) => {
      toast.error(error?.response?.data?.message || error.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteEmailTemplate,
    onSuccess: () => {
      toast.success(i18n.t('success.api.delete-item', { item: i18n.t('module.email-template') }));
      queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
    },
    onError: (error: ApiError) => {
      toast.error(error?.response?.data?.message || error.message);
    },
  });

  const { data: template } = useQuery({
    queryKey: ['emailTemplate', emailTemplateId],
    queryFn: () => fetchEmailTemplateById(emailTemplateId),
    enabled: !!emailTemplateId && hasAccount,
    retry: false,
  });

  const { data: templates, isLoading } = useQuery({
    queryKey: ['emailTemplates'],
    queryFn: fetchEmailTemplates,
    retry: false,
  });

  return {
    createMutation,
    updateMutation,
    deleteMutation,
    template,
    templates,
    isLoading,
  };
};